import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';
import TalukaCreate from '../../create/js/talukaCreate';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';
import { useApp } from '../../../../context/AppContext';
import { talukaAPI } from '../../../../utils/api';
import PropTypes from 'prop-types';

function TalukaTable({ onCreateTaluka, reloadKey = 0 }) {
  const { permissions, roles: userRoles } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedTaluka, setSelectedTaluka] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const isSuperAdmin = userRoles && (userRoles.includes('SUPER_ADMIN') || userRoles.includes('ROLE_SUPER_ADMIN'));
  const canCreate = permissions.includes('CREATE_TALUKA') || isSuperAdmin;
  const canEdit = permissions.includes('EDIT_TALUKA') || isSuperAdmin;
  const canDelete = permissions.includes('DELETE_TALUKA') || isSuperAdmin;

  const fetchTalukas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await talukaAPI.getAll();
      setData(res.data || []);
    } catch (err) {
      console.error('Failed to fetch talukas:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTalukas();
  }, [fetchTalukas, reloadKey]);

  const handleView = taluka => {
    setSelectedTaluka(taluka);
    setShowViewModal(true);
  };

  const handleEdit = taluka => {
    setSelectedTaluka(taluka);
    setShowEditModal(true);
  };

  const handleDelete = async (taluka) => {
    const result = await Swal.fire({
      title: 'Delete Taluka?',
      text: `Are you sure you want to delete ${taluka.name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await talukaAPI.delete(taluka.id);
        await fetchTalukas();
        Swal.fire('Deleted!', 'Taluka has been deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.message || 'Failed to delete taluka', 'error');
      }
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'taluka_id', header: 'Code', size: 100 },
    { accessorKey: 'name', header: 'Taluka Name', size: 250 },
    { 
      accessorKey: 'is_active', 
      header: 'Status', 
      size: 100,
      cell: ({ getValue }) => (
        <span className={`status-badge ${getValue() ? 'active' : 'inactive'}`}>
          {getValue() ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 150,
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view" onClick={() => handleView(row.original)}><FaEye /></button>
          {canEdit && <button className="eduss-global-action-btn edit" onClick={() => handleEdit(row.original)}><FaEdit /></button>}
          {canDelete && <button className="eduss-global-action-btn delete" onClick={() => handleDelete(row.original)}><FaTrash /></button>}
        </div>
      ),
    },
  ];

  const filteredData = data.filter(t => 
    t.name?.toLowerCase().includes(filter.toLowerCase()) || 
    t.taluka_id?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card">
      <TableToolbar 
        title="Talukas"
        subtitle="Manage administrative subdivisions (Talukas) and their status"
        onSearch={setFilter}
        onAdd={canCreate ? onCreateTaluka : null}
        addText="Add Taluka"
        showImportExport={true}
        onExport={() => talukaAPI.exportCsv().then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'talukas.csv');
          document.body.appendChild(link);
          link.click();
        })}
        onImport={async (file) => {
          const text = await file.text();
          await talukaAPI.importCsv(text);
          fetchTalukas();
          Swal.fire('Success', 'Talukas imported successfully', 'success');
        }}
        onDownloadSample={() => talukaAPI.sampleCsv().then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'talukas_sample.csv');
          document.body.appendChild(link);
          link.click();
        })}
      />

      <div style={{ padding: '0 30px 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
        />
      </div>

      {showViewModal && selectedTaluka && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Taluka Details</h3>
              <button onClick={() => setShowViewModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <table className="mini-info-table">
                <tbody>
                  <tr><th>ID</th><td>{selectedTaluka.id}</td></tr>
                  <tr><th>Code</th><td>{selectedTaluka.taluka_id}</td></tr>
                  <tr><th>Name</th><td>{selectedTaluka.name}</td></tr>
                  <tr><th>Status</th><td>{selectedTaluka.is_active ? 'Active' : 'Inactive'}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showEditModal && selectedTaluka && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Edit Taluka</h3>
              <button onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <TalukaCreate
                taluka={selectedTaluka}
                onClose={() => setShowEditModal(false)}
                onSubmit={() => { fetchTalukas(); setShowEditModal(false); }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

TalukaTable.propTypes = {
  onCreateTaluka: PropTypes.func,
  reloadKey: PropTypes.number
};

export default TalukaTable;
